import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import {
  BRAND, Icon, Button, Field, Input, Card, Stepper, Frame, IconChip,
} from '../../../components/onboarding/primitives';
import { paymentOrdersAPI } from '../../../services/api';

const STEP_LABELS = ['الحساب', 'المختبر', 'الفروع', 'الباقة', 'الأجهزة', 'الدفع', 'الإيصال'];
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED_MIME = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

/**
 * Props contract:
 *   state     — full onboarding state object. Must contain state.activeOrder.id
 *               (set by Step5 after submit() / getMyActive()).
 *   setState  — setter.
 *   goToStep  — (n: number) => void.
 *
 * API endpoints used:
 *   paymentOrdersAPI.uploadReceipt(orderId, formData)
 *     — POST /payment-orders/:id/receipt  (multipart/form-data)
 *     FormData fields: receipt (File), transferDate, transferLast4, notes
 *     200 -> { id, status: 'pending_review', ... }
 */
export default function Step7Receipt({ state, setState, goToStep, goBack }) {
  const d = state.s7 || {};
  // Functional updater so rapid successive updates (e.g. handleFile writes
  // file then transferDate immediately after) don't race on a stale
  // snapshot. Also doubles as stable-identity for the onChange callbacks
  // below which would otherwise rebind every render.
  const set = (k, v) => setState((prev) => ({
    ...prev,
    s7: { ...(prev.s7 || {}), [k]: v },
  }));

  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState('');
  const inputRef = useRef();
  // Preview data URL lives in component state only — it can be several MB and
  // must never touch the global onboarding state (OnboardingFlow serialises
  // state to localStorage and would trip QuotaExceededError). Gets rebuilt
  // automatically when the user re-picks a file after a refresh.
  const [preview, setPreview] = useState(null);

  // Backend returns the Mongoose doc so primary id arrives as `_id`. Keep the
  // other names as fallbacks in case a caller normalises the payload later.
  const orderId = state.activeOrder?._id
    || state.activeOrder?.id
    || state.activeOrder?.orderId
    || null;
  const total = Number(state.activeOrder?.total || 0);

  // If the user landed on Step 6 after a refresh (or deep-link login), the
  // activeOrder in local state may be stale or missing. Refetch from the
  // server so `orderId` and `total` line up with reality; the axios
  // interceptor already handles auth/account-status redirects.
  useEffect(() => {
    let cancelled = false;
    if (!orderId) {
      (async () => {
        try {
          const res = await paymentOrdersAPI.getMyActive();
          const data = res.data?.data || res.data || null;
          if (!cancelled && data) {
            setState((s) => ({ ...s, activeOrder: { ...(s.activeOrder || {}), ...data } }));
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[Step6Receipt] could not refetch active order:', e?.response?.status, e?.response?.data);
        }
      })();
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFile = (f) => {
    setFileError('');
    // eslint-disable-next-line no-console
    console.log('[Step6Receipt] handleFile received', f && {
      name: f.name, type: f.type, size: f.size, isFile: f instanceof File,
    });
    if (!f) {
      setFileError('لم يتم اختيار أي ملف');
      return;
    }
    // Some browsers (notably Safari on macOS/iOS) don't always populate
    // `file.type` — particularly for PDFs selected through the file
    // picker, or images shared from other apps. Fall back to checking the
    // file extension before rejecting as an unsupported type.
    const byExt = /\.(pdf|png|jpe?g)$/i.test(f.name || '');
    const byMime = ALLOWED_MIME.includes(f.type);
    if (!byExt && !byMime) {
      setFileError(`نوع الملف غير مدعوم (${f.type || 'غير معروف'}). استخدم PDF أو PNG أو JPG.`);
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setFileError('حجم الملف يتجاوز 5MB.');
      return;
    }
    // Only build a data URL for images — PDFs show as an icon card.
    const looksLikeImage = (f.type && f.type.startsWith('image/'))
      || /\.(png|jpe?g|webp)$/i.test(f.name || '');
    if (looksLikeImage) {
      const r = new FileReader();
      r.onload = () => setPreview(r.result);
      r.onerror = () => setPreview(null);
      try { r.readAsDataURL(f); } catch { setPreview(null); }
    } else {
      setPreview(null);
    }
    // Persist metadata + the raw File in global state. The raw File is NOT
    // JSON-serializable; on refresh it'll be `{}` and the user will have to
    // re-pick — acceptable because file picks must be explicit anyway.
    set('file', {
      name: f.name,
      size: f.size,
      type: f.type || (byExt ? (f.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg') : ''),
      raw: f,
    });
    // eslint-disable-next-line no-console
    console.log('[Step6Receipt] file saved to state', f.name);
  };

  // A File that round-tripped through JSON.stringify (localStorage) comes
  // back as a plain `{}`, not a File — and FormData would silently serialise
  // it as "[object Object]" which the server can't decode. Guard against
  // that: require a live File instance.
  const hasLiveFile = d.file?.raw instanceof File || d.file?.raw instanceof Blob;

  const canSubmit =
    hasLiveFile &&
    !!d.transferDate &&
    typeof d.transferLast4 === 'string' &&
    d.transferLast4.length === 4 &&
    !!orderId;

  const submit = async () => {
    if (uploading) return;
    if (!orderId) {
      toast.error('لا يوجد طلب نشط — ارجع لخطوة المراجعة والدفع أولاً');
      return;
    }
    if (!hasLiveFile) {
      // Usually triggered after a hard refresh — the picked file is gone.
      toast.error('يرجى اختيار ملف الإيصال من جديد');
      return;
    }
    if (!d.transferDate || !d.transferLast4 || d.transferLast4.length !== 4) {
      toast.error('يرجى إكمال تاريخ التحويل وآخر 4 أرقام من الحساب');
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('receipt', d.file.raw, d.file.name || 'receipt');
      fd.append('transferDate', d.transferDate);
      fd.append('transferLast4', d.transferLast4);
      if (d.notes) fd.append('notes', d.notes);

      const res = await paymentOrdersAPI.uploadReceipt(orderId, fd);
      const data = res.data?.data || res.data || {};
      setState((s) => ({
        ...s,
        activeOrder: { ...(s.activeOrder || {}), ...data },
      }));
      toast.success('تم رفع الإيصال بنجاح');
      goToStep(8);
    } catch (e) {
      // Surface the backend's reason verbatim — it's already Arabic. The
      // fallback catches network errors where response is undefined.
      const status = e?.response?.status;
      const serverMsg = e?.response?.data?.message
        || e?.response?.data?.errors?.[0]?.msg
        || e?.response?.data?.errors?.[0]?.message;
      // eslint-disable-next-line no-console
      console.error('[Step6Receipt] upload failed', status, e?.response?.data);
      if (status === 413) {
        toast.error('حجم الملف أكبر من الحد المسموح (5MB)');
      } else if (status === 400 && serverMsg) {
        toast.error(serverMsg);
      } else if (status === 404) {
        toast.error('الطلب غير موجود — ارجع لخطوة الدفع وأعد الإرسال');
      } else if (!status) {
        toast.error('انقطاع في الاتصال — تحقق من الشبكة وحاول مرة أخرى');
      } else {
        toast.error(serverMsg || 'تعذّر رفع الإيصال، حاول مرة أخرى');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Frame
      maxWidth={720}
      stepper={<Stepper steps={STEP_LABELS} current={6} />}
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => { if (goBack) goBack(); else goToStep(6); }}
            iconLeft={<Icon.Arrow dir="right" />}
            disabled={uploading}
          >
            رجوع لتعديل الطلب
          </Button>
          <Button onClick={submit} disabled={!canSubmit || uploading}>
            {uploading ? 'جاري الإرسال…' : 'إرسال للمصادقة'}
          </Button>
        </>
      }
    >
      <Card elevated padding={40}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
          <IconChip size={48}>
            <Icon.Check color={BRAND.greenDark} size={22} />
          </IconChip>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, letterSpacing: -0.4 }}>
              خطوة أخيرة قبل التفعيل
            </h1>
            <p style={{ fontSize: 14, color: BRAND.muted, margin: '4px 0 0' }}>
              ارفع إيصال التحويل لنصادق الدفع خلال ساعات العمل.
            </p>
          </div>
        </div>

        <div
          style={{
            background: BRAND.mint,
            borderRadius: 12,
            padding: 14,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '24px 0',
          }}
        >
          <div style={{ fontSize: 13, color: BRAND.greenDark }}>المبلغ المطلوب تحويله</div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: BRAND.greenDark,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {total.toLocaleString()} ر.س
          </div>
        </div>

        {/* Hidden file input lives OUTSIDE the <Field>'s implicit <label>.
            Some browsers (notably Safari on macOS) don't fire the file
            picker when a label wraps a `display:none` input — moving it
            to a sibling and driving it via `inputRef.current.click()`
            avoids that. `accept` uses MIME types only, matching the server
            whitelist. */}
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/jpg"
          style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }}
          onChange={(e) => {
            const f = e.target.files && e.target.files[0];
            handleFile(f);
            e.target.value = '';
          }}
        />

        <Field label="إيصال التحويل" required hint="PDF، PNG أو JPG حتى 5MB" error={fileError}>
          {d.file ? (
            <div
              style={{
                border: `1px solid ${BRAND.border}`,
                borderRadius: 14,
                padding: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                background: '#fff',
              }}
            >
              {d.file.type?.startsWith('image/') && preview ? (
                <img
                  src={preview}
                  alt="إيصال"
                  style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8 }}
                />
              ) : (
                <div
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 8,
                    background: BRAND.mint,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: BRAND.greenDark,
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  PDF
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {d.file.name}
                </div>
                <div style={{ fontSize: 12, color: BRAND.muted }}>
                  {(d.file.size / 1024).toFixed(0)} KB
                </div>
              </div>
              <button
                onClick={() => set('file', null)}
                disabled={uploading}
                style={{
                  background: 'transparent',
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 8,
                  width: 32,
                  height: 32,
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  color: BRAND.muted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: uploading ? 0.5 : 1,
                }}
              >
                <Icon.X />
              </button>
            </div>
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                // Prevent the surrounding <label>'s implicit click-to-focus
                // from also firing against the file input — in Safari that
                // double-triggers the picker and one of the two instances
                // may silently close without dispatching change.
                e.preventDefault();
                e.stopPropagation();
                inputRef.current?.click();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  inputRef.current?.click();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDrag(true);
              }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDrag(false);
                const f = e.dataTransfer.files && e.dataTransfer.files[0];
                handleFile(f);
              }}
              style={{
                border: `2px dashed ${drag ? BRAND.green : BRAND.border}`,
                background: drag ? BRAND.mint : BRAND.surfaceAlt,
                borderRadius: 16,
                padding: '40px 20px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all .15s',
                minHeight: 160,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
              }}
            >
              <IconChip size={48}>
                <Icon.Upload />
              </IconChip>
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                اسحب الإيصال هنا أو اضغط للاختيار
              </div>
              <div style={{ fontSize: 12.5, color: BRAND.muted }}>
                PDF · PNG · JPG — حتى 5MB
              </div>
              {/* Input is rendered above (sibling of Field) — intentionally
                  no duplicate here. */}
            </div>
          )}
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 18px' }}>
          <Field label="تاريخ التحويل" required>
            <Input
              type="date"
              value={d.transferDate || ''}
              onChange={(v) => set('transferDate', v)}
              ltr
            />
          </Field>
          <Field label="آخر 4 أرقام من الحساب المحوَّل منه" required>
            <Input
              value={d.transferLast4 || ''}
              onChange={(v) => set('transferLast4', (v || '').replace(/\D/g, '').slice(0, 4))}
              placeholder="1234"
              ltr
              maxLength={4}
            />
          </Field>
        </div>

        <Field label="ملاحظات" optional>
          <textarea
            value={d.notes || ''}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="أي تفاصيل تساعدنا في مصادقة التحويل"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: 14,
              fontSize: 14,
              fontFamily: 'inherit',
              border: `1px solid ${BRAND.border}`,
              borderRadius: 12,
              outline: 'none',
              minHeight: 80,
              resize: 'vertical',
              background: '#fff',
              color: BRAND.text,
            }}
          />
        </Field>

        {!orderId && (
          <div
            role="alert"
            style={{
              fontSize: 12.5,
              color: BRAND.error,
              marginTop: 8,
              background: 'rgba(226,75,74,0.06)',
              border: `1px solid rgba(226,75,74,0.2)`,
              borderRadius: 8,
              padding: '10px 12px',
            }}
          >
            لا يوجد طلب نشط — ارجع لخطوة المراجعة والدفع أولاً.
          </div>
        )}
      </Card>
    </Frame>
  );
}
